import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { isTauri } from '@/lib/utils/tauri';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const logger = createLogger('contexts/TauriFileDropContext');

interface DropZoneRegistration {
  id: string;
  element: HTMLElement;
  onDrop: (files: File[]) => void;
  accept?: string[];
}

interface TauriFileDropContextValue {
  registerDropZone: (registration: DropZoneRegistration) => () => void;
  isDragging: boolean;
  activeDropZoneId: string | null;
}

export const TauriFileDropContext = createContext<TauriFileDropContextValue>({
  registerDropZone: () => () => {},
  isDragging: false,
  activeDropZoneId: null,
});

interface TauriFileDropProviderProps {
  children: ReactNode;
}

export const TauriFileDropProvider: FC<TauriFileDropProviderProps> = ({ children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [activeDropZoneId, setActiveDropZoneId] = useState<string | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const dropZonesRef = useRef<Map<string, DropZoneRegistration>>(new Map());
  const unlistenersRef = useRef<(() => void)[]>([]);

  const registerDropZone = useCallback((registration: DropZoneRegistration) => {
    dropZonesRef.current.set(registration.id, registration);
    logger.info(`[TauriFileDrop] Registered drop zone: ${registration.id}`);

    return () => {
      dropZonesRef.current.delete(registration.id);
      logger.info(`[TauriFileDrop] Unregistered drop zone: ${registration.id}`);
    };
  }, []);

  // Ref for the overlay element so we can hide it during point detection
  const overlayRef = useRef<HTMLDivElement>(null);

  // Find which drop zone contains the given point
  const findDropZoneAtPoint = useCallback((x: number, y: number): DropZoneRegistration | null => {
    // Temporarily hide overlay to get element beneath it
    if (overlayRef.current) {
      overlayRef.current.style.display = 'none';
    }

    const element = document.elementFromPoint(x, y);

    // Restore overlay
    if (overlayRef.current) {
      overlayRef.current.style.display = '';
    }

    if (!element) return null;

    // Strategy 1: Check if any drop zone contains the element at point
    let bestMatch: DropZoneRegistration | null = null;
    let bestDepth = -1;

    for (const [, registration] of dropZonesRef.current) {
      if (registration.element.contains(element) || registration.element === element) {
        // Calculate depth by counting parents to find the most specific match
        let depth = 0;
        let parent: HTMLElement | null = registration.element;
        while (parent) {
          depth++;
          parent = parent.parentElement;
        }
        if (depth > bestDepth) {
          bestDepth = depth;
          bestMatch = registration;
        }
      }
    }

    if (bestMatch) return bestMatch;

    // Strategy 2: Walk up from the element to see if any ancestor is a drop zone
    let current: HTMLElement | null = element as HTMLElement;
    while (current) {
      for (const [, registration] of dropZonesRef.current) {
        if (registration.element === current) {
          return registration;
        }
      }
      current = current.parentElement;
    }

    // Strategy 3: Check if point is within any drop zone's bounding rect
    for (const [, registration] of dropZonesRef.current) {
      const rect = registration.element.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        // Point is within this drop zone's bounds
        if (!bestMatch) {
          bestMatch = registration;
          bestDepth = 0;
        } else {
          // Prefer smaller (more specific) drop zones
          const bestRect = bestMatch.element.getBoundingClientRect();
          const bestArea = bestRect.width * bestRect.height;
          const currentArea = rect.width * rect.height;
          if (currentArea < bestArea) {
            bestMatch = registration;
          }
        }
      }
    }

    return bestMatch;
  }, []);

  // Convert file paths to File objects using fetch
  const pathsToFiles = useCallback(async (paths: string[]): Promise<File[]> => {
    const files: File[] = [];

    for (const path of paths) {
      try {
        // Use Tauri's fs plugin to read the file
        const { readFile } = await import('@tauri-apps/plugin-fs');
        const contents = await readFile(path);

        // Extract filename from path
        const fileName = path.split('/').pop() || path.split('\\').pop() || 'file';

        // Guess MIME type from extension
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        const mimeTypes: Record<string, string> = {
          'csv': 'text/csv',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'xls': 'application/vnd.ms-excel',
          'pdf': 'application/pdf',
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'gif': 'image/gif',
          'webp': 'image/webp',
          'svg': 'image/svg+xml',
          'txt': 'text/plain',
          'json': 'application/json',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };
        const mimeType = mimeTypes[ext] || 'application/octet-stream';

        const file = new File([contents], fileName, { type: mimeType });
        files.push(file);
      } catch (error) {
        logger.error(`[TauriFileDrop] Failed to read file: ${path}`, error);
      }
    }

    return files;
  }, []);

  useEffect(() => {
    if (!isTauri()) return;

    const setupListeners = async () => {
      try {
        const { listen } = await import('@tauri-apps/api/event');

        // Tauri 2.0 drag events with position
        const unlistenDragEnter = await listen<{ paths: string[]; position: { x: number; y: number } }>(
          'tauri://drag-enter',
          (event) => {
            logger.info('[TauriFileDrop] Drag enter', event.payload);
            setIsDragging(true);
            if (event.payload.position) {
              setDragPosition(event.payload.position);
              const zone = findDropZoneAtPoint(event.payload.position.x, event.payload.position.y);
              setActiveDropZoneId(zone?.id || null);
            }
          }
        );

        const unlistenDragOver = await listen<{ paths: string[]; position: { x: number; y: number } }>(
          'tauri://drag-over',
          (event) => {
            if (event.payload.position) {
              setDragPosition(event.payload.position);
              const zone = findDropZoneAtPoint(event.payload.position.x, event.payload.position.y);
              setActiveDropZoneId(zone?.id || null);
            }
          }
        );

        const unlistenDragLeave = await listen('tauri://drag-leave', () => {
          logger.info('[TauriFileDrop] Drag leave');
          setIsDragging(false);
          setActiveDropZoneId(null);
          setDragPosition(null);
        });

        const unlistenDrop = await listen<{ paths: string[]; position: { x: number; y: number } }>(
          'tauri://drag-drop',
          async (event) => {
            logger.info('[TauriFileDrop] Drop', event.payload);
            setIsDragging(false);
            setDragPosition(null);

            const { paths, position } = event.payload;
            if (!paths?.length) {
              setActiveDropZoneId(null);
              return;
            }

            // Find the drop zone at the drop position
            const zone = findDropZoneAtPoint(position.x, position.y);
            setActiveDropZoneId(null);

            if (zone) {
              logger.info(`[TauriFileDrop] Dropping ${paths.length} files on zone: ${zone.id}`);

              // Filter files by accepted types if specified
              let filteredPaths = paths;
              if (zone.accept?.length) {
                filteredPaths = paths.filter(path => {
                  const ext = '.' + (path.split('.').pop()?.toLowerCase() || '');
                  return zone.accept!.some(accept => {
                    // Handle wildcards like image/*
                    if (accept.endsWith('/*')) {
                      const category = accept.split('/')[0];
                      const mimeCategories: Record<string, string[]> = {
                        'image': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp'],
                        'text': ['.txt', '.csv', '.md', '.json', '.xml'],
                        'application': ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'],
                      };
                      return mimeCategories[category]?.includes(ext);
                    }
                    // Handle specific extensions
                    if (accept.startsWith('.')) {
                      return ext === accept.toLowerCase();
                    }
                    // Handle MIME types
                    const extFromMime: Record<string, string[]> = {
                      'text/csv': ['.csv'],
                      'application/pdf': ['.pdf'],
                      'image/png': ['.png'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                    };
                    return extFromMime[accept]?.includes(ext);
                  });
                });
              }

              if (filteredPaths.length > 0) {
                const files = await pathsToFiles(filteredPaths);
                if (files.length > 0) {
                  zone.onDrop(files);
                }
              } else {
                logger.warn('[TauriFileDrop] No files matched the accepted types');
              }
            } else {
              logger.info('[TauriFileDrop] No drop zone found at position');
            }
          }
        );

        unlistenersRef.current = [unlistenDragEnter, unlistenDragOver, unlistenDragLeave, unlistenDrop];
      } catch (error) {
        logger.error('[TauriFileDrop] Failed to set up listeners', error);
      }
    };

    setupListeners();

    return () => {
      unlistenersRef.current.forEach(unlisten => unlisten());
      unlistenersRef.current = [];
    };
  }, [findDropZoneAtPoint, pathsToFiles]);

  return (
    <TauriFileDropContext value={{ registerDropZone, isDragging, activeDropZoneId }}>
      {children}
      {/* Full-page overlay when dragging in Tauri */}
      {isTauri() && isDragging && (
        <div
          ref={overlayRef}
          className={cn(
            "fixed inset-0 z-[9999] pointer-events-none",
            "bg-primary/5 backdrop-blur-[1px]",
            "transition-opacity duration-150"
          )}
        >
          {/* Visual indicator for the active drop zone */}
          {activeDropZoneId && dragPosition && (
            <div
              className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/50"
              style={{ left: dragPosition.x, top: dragPosition.y }}
            />
          )}

          {/* Centered hint when no specific zone is hovered */}
          {!activeDropZoneId && dropZonesRef.current.size > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-background/95 border border-primary/50 rounded-lg px-6 py-4 shadow-lg">
                <p className="text-sm font-medium text-foreground">
                  Drop files on a drop zone
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </TauriFileDropContext>
  );
};
